import { v4 as uuid } from 'uuid';
import { UUID } from '@hfc/common/src/types/UUID';
import { ServicesApiResponse, Ploc } from '@hfc/ui-core';
import { CalendlyEvent } from '@/consent/medicare/domain/CalendlyEventResponse';
import { MedicareConsent } from '@/consent/medicare/domain/MedicareConsent';
import { MedicareConsentApi } from '@/consent/medicare/infrastructure/dataSource/MedicareConsentApi';
import { SubmitConsentUseCase } from '@/consent/medicare/application/SubmitConsentUseCase';
import { MedicareContactInfo } from '@/consent/medicare/domain/MedicareContactInfo';
import { GetConsentUseCase } from '@/consent/medicare/application/GetConsentUseCase';
import { SaveAndExitConsentUseCase } from '@/consent/medicare/application/SaveAndExitConsentUseCase';
import { UpdateConsentReferenceIdUseCase } from '@/consent/medicare/application/UpdateConsentReferenceIdUseCase';

const medicareConsentRepository = new MedicareConsentApi();
const getMedicareConsent = new GetConsentUseCase(medicareConsentRepository);
const submitMedicareConsent = new SubmitConsentUseCase(medicareConsentRepository);
const saveAndExitConsent = new SaveAndExitConsentUseCase(medicareConsentRepository);
const updateConsentReferenceId = new UpdateConsentReferenceIdUseCase(medicareConsentRepository);

export interface MedicareConsentStoreInterface {
  application: MedicareConsent,
  loadingFromDb: boolean,
  isSubmittingCalendlyInfo: boolean,
  isSubmitting: boolean,
  loading: boolean,
}

const initialState: MedicareConsentStoreInterface = {
  application: {
    id: uuid() as UUID,
    appointment_id: '',
    agent: '',
    invitee_id: '',
    signature: {
      first: '',
      last: '',
    },
    appointment_scopes: [],
    captcha_token: '',
    contactInfo: {} as MedicareContactInfo,
    aep: false,
  },
  isSubmittingCalendlyInfo: false,
  isSubmitting: false,
  loadingFromDb: false,
  loading: false,
};

export class MedicareConsentStore extends Ploc<MedicareConsentStoreInterface> {

  public constructor () {
    super(initialState);
  }

  public async initAppFromDatabase (medicareConsent: MedicareConsent): Promise<void> {
    await this.updateForm(medicareConsent);
  }

  public async getMedicareConsentData (uuid: UUID): Promise<ServicesApiResponse<MedicareConsent>> {
    this.changeState({ ...this.state, loading: true });
    const response = await getMedicareConsent.run(uuid);

    if (response.success && response.data) {
      const medicareConsent = response.data;
      medicareConsent.id = uuid;
      this.initAppFromDatabase(medicareConsent);
    }
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async submit (): Promise<ServicesApiResponse> {
    this.changeState({ ...this.state, isSubmitting: true });
    const result = await submitMedicareConsent.run(this.state.application);
    this.changeState({ ...this.state, isSubmitting: false });

    return result;
  }

  public async updateReferenceId (): Promise<ServicesApiResponse> {
    this.changeState({ ...this.state, isSubmittingCalendlyInfo: true });
    const result = await updateConsentReferenceId.run(this.state.application);
    this.changeState({ ...this.state, isSubmittingCalendlyInfo: false });

    return result;
  }

  public async saveAndExit (uuid: UUID, email: string|null, phone: number|null, medicareConsent: MedicareConsent): Promise<ServicesApiResponse<MedicareConsent>> {
    this.changeState({ ...this.state, loading: true });
    const response = await saveAndExitConsent.run([uuid, email, phone, medicareConsent]);
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async updateMedicareConsentStore (value: Partial<MedicareConsentStoreInterface>): Promise<void> {
    this.changeState({ ...this.state, ...value });
  }

  public async updateForm (value: Partial<MedicareConsent>): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, ...value } });
  }

  public async updateContactInfo (value: Partial<MedicareContactInfo>): Promise<void> {
    this.updateForm({ contactInfo: { ...this.state.application.contactInfo, ...value } });
  }

  public async saveEventData (calendlyEvent: CalendlyEvent): Promise<void> {
    this.updateForm({ appointment_id: calendlyEvent.appointment_id, invitee_id: calendlyEvent.invitee_id });
  }

  public clearAndRestart (): void {
    this.resetState();
    localStorage.removeItem('smallBusinessApplication');
    this.changeState({ ...this.state, application: { ...this.state.application, id: uuid() } });
  }

}
